import type { ReactNode } from 'react';
import { LogOut } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useTheme, type ThemeMode } from '@/hooks/useTheme';
import ThemeColorPicker from './ThemeColorPicker';
import WebhookSettingsPanel from './WebhookSettingsPanel';
import { notifyUnauthorized } from '@/shared/auth-session';
import {
  getWorkspaceRepoIdFromPath,
  isComponentsListPath,
} from './workspace-route-utils';

const THEME_OPTIONS: { id: ThemeMode; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'System' },
];

function readAuthAccount(): { name: string; email: string; role: string } {
  try {
    const raw =
      sessionStorage.getItem('qtrace-auth') ??
      localStorage.getItem('qtrace-auth');
    if (!raw) {
      return { name: '—', email: '—', role: 'User' };
    }
    const session = JSON.parse(raw) as {
      name?: string;
      email?: string;
    };
    return {
      name: session.name?.trim() || '—',
      email: session.email?.trim() || '—',
      role: 'Admin',
    };
  } catch {
    return { name: '—', email: '—', role: 'User' };
  }
}

function SettingsSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h3 className="m-0 mb-1.5 text-[0.6rem] font-bold uppercase tracking-[0.1em] text-[var(--inv-text-muted)]">
        {title}
      </h3>
      <div className="overflow-hidden rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface)]">
        {children}
      </div>
    </section>
  );
}

function SettingsRow({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border-soft)] px-3 py-2 last:border-b-0',
        className,
      )}>
      <span className="shrink-0 text-[0.8125rem] text-[var(--inv-text)]">
        {label}
      </span>
      <div className="min-w-0 text-right">{children}</div>
    </div>
  );
}

function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: { id: T; label: string }[];
  onChange: (next: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      className="inline-flex rounded-[8px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] p-0.5"
      role="group"
      aria-label={ariaLabel}>
      {options.map(option => {
        const active = value === option.id;
        return (
          <Button
            key={option.id}
            type="button"
            variant="ghost"
            className={cn(
              'h-auto rounded-md px-2 py-1 text-[0.75rem] hover:bg-[var(--inv-surface)]',
              active
                ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))]'
                : 'text-[var(--inv-text-secondary)] hover:text-[var(--inv-text)]',
            )}
            aria-pressed={active}
            onClick={() => onChange(option.id)}>
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}

interface SettingsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SettingsPanel = ({ open, onOpenChange }: SettingsPanelProps) => {
  const { themeMode, setThemeMode } = useTheme();
  const location = useLocation();
  const workspaceRepoId = isComponentsListPath(location.pathname)
    ? undefined
    : getWorkspaceRepoIdFromPath(location.pathname);
  const showWebhookSettings = Boolean(workspaceRepoId);

  const account = readAuthAccount();

  const handleSignOut = () => {
    onOpenChange(false);
    notifyUnauthorized();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="gap-0 p-0">
        <SheetHeader className="shrink-0 border-0 border-b border-solid border-[var(--inv-border-soft)] px-4 pb-3 pt-4">
          <SheetTitle>Settings</SheetTitle>
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
          <SettingsSection title="Appearance">
            <SettingsRow label="Theme">
              <SegmentedControl
                value={themeMode}
                options={THEME_OPTIONS}
                onChange={setThemeMode}
                ariaLabel="Theme"
              />
            </SettingsRow>
            <div className="px-3 py-3">
              <p className="m-0 mb-2 text-[0.8125rem] text-[var(--inv-text)]">
                Accent colour
              </p>
              <ThemeColorPicker />
            </div>
          </SettingsSection>

          {showWebhookSettings ? (
            <SettingsSection title="Webhooks">
              <WebhookSettingsPanel active={open} repoId={workspaceRepoId} />
            </SettingsSection>
          ) : null}

          <SettingsSection title="Account">
            <SettingsRow label="Name">
              <span className="text-[0.8125rem] text-[var(--inv-text-secondary)]">
                {account.name}
              </span>
            </SettingsRow>
            <SettingsRow label="Email">
              <span className="text-[0.8125rem] text-[var(--inv-text-secondary)]">
                {account.email}
              </span>
            </SettingsRow>
            <SettingsRow label="Role">
              <span className="text-[0.8125rem] text-[var(--inv-text-secondary)]">
                {account.role}
              </span>
            </SettingsRow>
            <div className="space-y-1.5 border-0 border-t border-solid border-[var(--inv-border-soft)] px-3 py-2.5">
              {/* <Button
                type="button"
                variant="outline"
                className="h-8 w-full text-[0.8125rem] border-[var(--inv-border)] text-[var(--inv-text)]">
                Reset password
              </Button> */}
              <Button
                type="button"
                variant="outline"
                className="h-8 w-full text-[0.8125rem] border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={handleSignOut}>
                <LogOut className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Sign out
              </Button>
            </div>
          </SettingsSection>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SettingsPanel;
